/**
 * The board flinching.
 *
 * An impact that leaves the figures standing perfectly still reads as a picture
 * played over them rather than as something that happened to them. This is the one
 * channel through which an effect is allowed to touch the rest of the scene: it
 * says "this cell was hit this hard", and the token layer and the pedestal stage
 * each decide for themselves what that does to what they draw.
 *
 * Deliberately a tiny shared store rather than events: both readers are inside a
 * render loop that runs anyway, so they sample the current offset when they need it
 * and nothing has to subscribe, unsubscribe, or survive a component being replaced.
 */

const DEFAULT_MS = 420;

/** Per-subject shake, so two figures hit by the same blast do not shudder in step. */
function seedOf(key) {
  let hash = 0;
  const text = String(key);
  for (let i = 0; i < text.length; i++) {
    hash = (Math.imul(hash, 31) + text.charCodeAt(i)) | 0;
  }
  return (hash >>> 0) / 4294967296;
}

function normalize(vector) {
  if (!vector) return null;
  const length = Math.hypot(vector.x || 0, vector.y || 0);
  if (!(length > 0)) return null;
  return { x: (vector.x || 0) / length, y: (vector.y || 0) / length };
}

/** The one key the whole board shakes under, so nothing else can claim it. */
export const BOARD_SHAKE_KEY = 'board';

/**
 * Below this weight the ground does not move.
 *
 * The difference between a hit and a cataclysm is the only thing this number states.
 * A rank-9 spark on one cell weighs ~0.33 and must leave the world exactly where it
 * was; a twenty-five cell hellfire weighs 1 and should move the camera. Anything
 * that shook the board on every jab would turn the whole fight into a rattle.
 */
export const GROUND_SHAKE_THRESHOLD = 0.35;
export const GROUND_SHAKE_MS = 620;

/** How hard the ground is hit by a cast, 0 for everything that is not heavy. */
export function groundShakeForce(cast) {
  const weight = Number(cast?.weight) || 0;
  const heft = (weight - GROUND_SHAKE_THRESHOLD) / (1 - GROUND_SHAKE_THRESHOLD);
  if (heft <= 0) return 0;
  const force = Number(cast?.impactForce) || 0;
  return Math.min(1.8, heft * (0.5 + Math.min(1.5, force)));
}

class ImpactShake {
  constructor() {
    this.active = new Map();
  }

  /**
   * @param key        what was hit: an object id, or a cell key for the ground
   * @param force      0..2ish. 1 is a solid hit, 0.2 is a graze, 1.5 is a hammer
   * @param durationMs how long it rings for
   * @param direction  where the blow came FROM, as a grid vector pointing at the
   *                   subject. Given one, the body is thrown along it and rings back
   *                   along the same line — a recoil. Without one it shudders on its
   *                   own seeded axis, which is what a blast that surrounds it does.
   */
  hit(key, force = 1, durationMs = DEFAULT_MS, direction = null) {
    if (!key || force <= 0) return;
    const now = performance.now();
    this.sweep(now);
    const existing = this.active.get(key);
    // A second hit on something already shaking adds to it rather than restarting
    // it, so a burst that catches someone twice lands twice as hard.
    const amplitude = Math.min(2.4, (existing ? existing.amplitude * existing.remaining(now) : 0) + force);
    this.active.set(key, {
      start: now,
      duration: durationMs,
      amplitude,
      seed: seedOf(key),
      direction: normalize(direction),
      remaining(at) {
        return Math.max(0, 1 - (at - this.start) / this.duration);
      },
    });
  }

  /**
   * The current displacement, in units of one cell's on-screen width.
   *
   * A decaying oscillation rather than random jitter: noise reads as a rendering
   * fault, while a ring-down reads as mass being moved and coming back to rest.
   */
  offset(key, now = performance.now()) {
    const shake = this.active.get(key);
    if (!shake) return null;
    const life = Math.max(0, (now - shake.start) / shake.duration);
    if (life >= 1) {
      this.active.delete(key);
      return null;
    }
    const decay = Math.pow(1 - life, 2.2);
    const directed = shake.direction;
    /*
     * A recoil starts at rest and is thrown AWAY on its first swing, so a directed
     * ring carries no phase offset and rings fewer times: the body is knocked back
     * and returns, where the seeded phase would have it flinch into the blow.
     *
     * Two and a bit cycles, not five: a body knocked back, coming through its own
     * position, and a much smaller second swing that dies. More than that and the
     * recoil stops reading as weight being moved and starts reading as a vibration,
     * which is the same fault as random jitter arrived at from the other side.
     */
    const ring = directed
      ? Math.sin(life * Math.PI * 2 * 2.25)
      : Math.sin(life * Math.PI * 2 * 5.5 + shake.seed * 9.0);
    const angle = shake.seed * Math.PI * 2;
    const axis = directed || { x: Math.cos(angle), y: Math.sin(angle) };
    const amplitude = shake.amplitude * decay * 0.16;
    return {
      x: axis.x * ring * amplitude,
      y: axis.y * ring * amplitude * 0.55,
      /* Vertical lift, for a figure knocked upward rather than sideways. */
      lift: Math.max(0, ring) * amplitude * 0.4,
      decay,
    };
  }

  /**
   * Drop what has rung out.
   *
   * `offset` evicts the key it was asked for, which is enough for a subject someone
   * is drawing — but not every key is read. The stage records the CELL that was hit
   * (`cell:col,row`) so a board can flinch the ground under a blast; no board reads
   * that yet, and an entry nobody queries would otherwise sit in this map for the
   * life of the SPA. Swept from `hit`, so the cost is one pass per cast over a map
   * that is only ever a few entries wide.
   */
  sweep(now = performance.now()) {
    this.active.forEach((shake, key) => {
      if (now - shake.start >= shake.duration) this.active.delete(key);
    });
  }

  isShaking(key) {
    return this.active.has(key);
  }

  get size() {
    return this.active.size;
  }

  clear() {
    this.active.clear();
  }
}

export const impactShake = new ImpactShake();
export default impactShake;
